import { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router";
import {
  Box, Paper, Stack, Typography, Alert, Link, MenuItem, Tabs, Tab,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { FormField, LoadingButton } from "@safrater/ui";
import { useAuth } from "../../api/auth-context";

interface PasswordForm { identifier: string; password: string; tenantId?: string }
interface OtpEmailForm { email: string }
interface OtpCodeForm { code: string; tenantId?: string }

// "Lembrar de mim": guarda só o email/CPF (nunca a senha) p/ pré-preencher.
const REMEMBER_KEY = "safrater_remembered_id";
const getRememberedId = (): string => { try { return localStorage.getItem(REMEMBER_KEY) ?? ""; } catch { return ""; } };

export function LoginPage() {
  const { loginWithPassword, sendOtp, verifyOtp, setStaySignedIn, accessToken, ready, tenants } = useAuth();
  const [mode, setMode] = useState<"password" | "otp">("password");
  const [otpStep, setOtpStep] = useState<"request" | "verify">("request");
  const [otpEmail, setOtpEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const remembered = getRememberedId();
  const [rememberMe, setRememberMe] = useState<boolean>(!!remembered);
  const [stayOpen, setStayOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const pw = useForm<PasswordForm>({ defaultValues: { identifier: remembered, password: "" } });
  const oe = useForm<OtpEmailForm>({ defaultValues: { email: "" } });
  const oc = useForm<OtpCodeForm>({ defaultValues: { code: "" } });

  // Se já tem sessão (cookie de refresh válido recuperou), redireciona.
  useEffect(() => {
    if (ready && accessToken) navigate("/", { replace: true });
  }, [ready, accessToken, navigate]);

  if (!ready) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <Typography variant="body2" color="text.secondary">Verificando sessão…</Typography>
      </Box>
    );
  }

  const handleMessage = (e: unknown, fallback: string) => {
    const msg = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join("; ");
    return msg ?? fallback;
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2, bgcolor: "background.default" }}>
      <Paper sx={{ p: 4, maxWidth: 440, width: "100%" }}>
        <Typography variant="h4" align="center" sx={{ mb: 1 }}>Safrater</Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Central de Acessos
        </Typography>

        <Tabs
          value={mode}
          onChange={(_, v) => { setMode(v); setError(null); setInfo(null); setOtpStep("request"); }}
          centered
          sx={{ mb: 2 }}
        >
          <Tab value="password" label="Senha" />
          <Tab value="otp" label="Código por email" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

        {/* ─── Login por senha ──────────────────────────────────────────── */}
        {mode === "password" && (
          <Stack
            component="form"
            spacing={2}
            onSubmit={pw.handleSubmit(async (data) => {
              setError(null);
              try {
                const r = await loginWithPassword(data.identifier, data.password, data.tenantId || undefined);
                // "Lembrar de mim": persiste/limpa o identificador (nunca a senha).
                try {
                  if (rememberMe) localStorage.setItem(REMEMBER_KEY, data.identifier);
                  else localStorage.removeItem(REMEMBER_KEY);
                } catch { /* */ }
                if (r.requiresTenantChoice) {
                  setInfo("Você tem acesso a múltiplas instituições. Selecione uma e entre novamente.");
                  return;
                }
                // Pergunta se deseja permanecer conectado antes de seguir.
                setStayOpen(true);
              } catch (e) {
                setError(handleMessage(e, "Não foi possível fazer login."));
              }
            })}
          >
            <FormField
              label="Email ou CPF"
              autoFocus
              autoComplete="username"
              {...pw.register("identifier", { required: "Informe seu email ou CPF" })}
              errorMessage={pw.formState.errors.identifier?.message}
            />
            <FormField
              label="Senha"
              type="password"
              autoComplete="current-password"
              {...pw.register("password", { required: "Senha obrigatória", minLength: { value: 8, message: "Mínimo 8 caracteres" } })}
              errorMessage={pw.formState.errors.password?.message}
            />
            {tenants.length > 1 && (
              <FormField select label="Instituição" {...pw.register("tenantId")} defaultValue="">
                <MenuItem value="">Selecionar…</MenuItem>
                {tenants.map((t) => <MenuItem value={t.id} key={t.id}>{t.name}</MenuItem>)}
              </FormField>
            )}
            <FormControlLabel
              control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />}
              label="Lembrar de mim"
            />
            <LoadingButton type="submit" variant="contained" size="large" loading={pw.formState.isSubmitting} fullWidth>
              Entrar
            </LoadingButton>
            <Stack direction="row" justifyContent="space-between">
              <Link component={RouterLink} to="/signup">Cadastrar minha instituição</Link>
              <Link component="button" type="button" onClick={() => navigate("/forgot")}>Esqueci minha senha</Link>
            </Stack>
          </Stack>
        )}

        {/* ─── Login por OTP (sem senha) ────────────────────────────────── */}
        {mode === "otp" && otpStep === "request" && (
          <Stack
            component="form"
            spacing={2}
            onSubmit={oe.handleSubmit(async (data) => {
              setError(null);
              try {
                await sendOtp(data.email);
                setOtpEmail(data.email);
                setOtpStep("verify");
                setInfo("Se o email está cadastrado, um código foi enviado. Ele é válido por 3 minutos.");
              } catch (e) {
                setError(handleMessage(e, "Não foi possível enviar o código."));
              }
            })}
          >
            <Typography variant="body2" color="text.secondary">
              Informe seu email. Vamos enviar um código de 6 dígitos.
            </Typography>
            <FormField
              label="Email"
              type="email"
              autoFocus
              autoComplete="email"
              {...oe.register("email", { required: "Informe seu email" })}
              errorMessage={oe.formState.errors.email?.message}
            />
            <LoadingButton type="submit" variant="contained" size="large" loading={oe.formState.isSubmitting} fullWidth>
              Enviar código
            </LoadingButton>
            <Stack direction="row" justifyContent="space-between">
              <Link component={RouterLink} to="/signup">Cadastrar minha instituição</Link>
              <Link component="button" type="button" onClick={() => setMode("password")}>Usar senha</Link>
            </Stack>
          </Stack>
        )}

        {mode === "otp" && otpStep === "verify" && (
          <Stack
            component="form"
            spacing={2}
            onSubmit={oc.handleSubmit(async (data) => {
              setError(null);
              try {
                const r = await verifyOtp(otpEmail, data.code, data.tenantId || undefined);
                if (r.requiresTenantChoice) {
                  setInfo("Você tem acesso a múltiplas instituições. Selecione uma e entre novamente.");
                  return;
                }
                navigate("/", { replace: true });
              } catch (e) {
                setError(handleMessage(e, "Código inválido."));
              }
            })}
          >
            <Typography variant="body2">
              Digite o código de 6 dígitos enviado para <strong>{otpEmail}</strong>.
            </Typography>
            <FormField
              label="Código"
              autoFocus
              inputProps={{ inputMode: "numeric", maxLength: 6, autoComplete: "one-time-code" }}
              {...oc.register("code", { required: "Informe o código", minLength: { value: 6, message: "6 dígitos" }, maxLength: 6 })}
              errorMessage={oc.formState.errors.code?.message}
            />
            {tenants.length > 1 && (
              <FormField select label="Instituição" {...oc.register("tenantId")} defaultValue="">
                <MenuItem value="">Selecionar…</MenuItem>
                {tenants.map((t) => <MenuItem value={t.id} key={t.id}>{t.name}</MenuItem>)}
              </FormField>
            )}
            <LoadingButton type="submit" variant="contained" size="large" loading={oc.formState.isSubmitting} fullWidth>
              Entrar
            </LoadingButton>
            <Stack direction="row" justifyContent="space-between">
              <Link component="button" type="button" onClick={async () => {
                setError(null);
                try {
                  await sendOtp(otpEmail);
                  setInfo("Novo código enviado.");
                } catch (e) {
                  setError(handleMessage(e, "Falha ao reenviar."));
                }
              }}>Reenviar código</Link>
              <Link component="button" type="button" onClick={() => { setOtpStep("request"); setInfo(null); setError(null); }}>
                Mudar email
              </Link>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* Após logar: pergunta se deseja permanecer conectado (sessão longa) */}
      <Dialog open={stayOpen} onClose={() => { setStayOpen(false); navigate("/", { replace: true }); }}>
        <DialogTitle>Permanecer conectado?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja permanecer conectado neste dispositivo? Sua sessão será mantida por vários dias
            e renovada automaticamente. Use apenas em dispositivos de sua confiança.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={async () => { await setStaySignedIn(false); setStayOpen(false); navigate("/", { replace: true }); }}>
            Agora não
          </Button>
          <Button variant="contained" onClick={async () => { await setStaySignedIn(true); setStayOpen(false); navigate("/", { replace: true }); }}>
            Sim, manter conectado
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
